/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { ShopperProducts } from '@/scapi';
import { widenImageUrl } from '@/lib/images/widen-image';

/**
 * Readers for the Black Magic product-record custom fields surfaced on the PDP (UCR-2697).
 *
 * SCAPI serializes product custom attributes with a `c_` prefix and returns them inline on the
 * Shopper Products `Product` document, so a field with ID `pdpMainImage` reads as `product.c_pdpMainImage`.
 * The fields only hold values — display labels are supplied here (see PRODUCT_INFORMATION_FIELDS).
 */
type Product = ShopperProducts.schemas['Product'];

/** Read a non-empty trimmed string custom field (`c_<fieldId>`) off the product. */
export function readProductField(product: Product, fieldId: string): string | undefined {
    const value = (product as Record<string, unknown>)[`c_${fieldId}`];
    return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

/** Brand display name shown as the PDP eyebrow. May contain HTML markup (rendered as-is). */
export const BRAND_DISPLAY_NAME_FIELD = 'brandDisplayName';

/**
 * Primary product image (a Widen CDN URL) — the resting PDP gallery image, and the product's
 * canonical image for SEO. This is the source of truth for product imagery: SCAPI returns
 * `imageGroups: []` for these products because the DAM feeds these attributes, not DIS (UCR-3003).
 */
export const PDP_MAIN_IMAGE_FIELD = 'pdpMainImage';

/**
 * The product's main DAM image as an absolute Widen CDN URL, with any stored query string dropped
 * (the DAM stores these with a trailing `?`), so consumers get the full-size asset rather than a
 * resized derivative. Shared by the product JSON-LD (UCR-3003) and `og:image` (UCR-3023) so a
 * product's rich result and its social card always show the same picture.
 */
export function getDamMainImageUrl(product: Product): string | undefined {
    return widenImageUrl(readProductField(product, PDP_MAIN_IMAGE_FIELD)) || undefined;
}

/** "Shop on Amazon" link. */
export const AMAZON_URL_FIELD = 'amazonURL';

/** "What's Included" specification field IDs, in display order. */
export const SPECIFICATION_FIELDS = [
    'specification1',
    'specification2',
    'specification3',
    'specification4',
    'specification5',
] as const;

/** Title for the "What's Included" section; falls back to "What's Included" when empty. */
export const SPECIFICATION_TITLE_FIELD = 'specificationTitle';

/** "Product Details" accordion field IDs, in display order. */
export const PRODUCT_DETAIL_FIELDS = [
    'productDetail1',
    'productDetail2',
    'productDetail3',
    'productDetail4',
    'productDetail5',
] as const;

/** Title for the "Product Details" accordion; falls back to "Product Details" when empty. */
export const PRODUCT_DETAIL_TITLE_FIELD = 'productDetailTitle';

/** The populated "What's Included" specifications for a product, in order (empty fields skipped). */
export function getSpecifications(product: Product): string[] {
    return SPECIFICATION_FIELDS.map((field) => readProductField(product, field)).filter((value): value is string =>
        Boolean(value)
    );
}

/** The populated "Product Details" rows for a product, in order (empty fields skipped). */
export function getProductDetails(product: Product): string[] {
    return PRODUCT_DETAIL_FIELDS.map((field) => readProductField(product, field)).filter((value): value is string =>
        Boolean(value)
    );
}
